import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Col, Form, Input, Radio, Row } from "antd"
import React from "react"
import type { WrappedFormUtils } from "antd/lib/form/Form"
import DrawerDetailFormHeader from "../../../uiKit/Drawer/components/DrawerDetailFormHeader"
import DrawerDetailFormFooter from "../../../uiKit/Drawer/components/DrawerDetailFormFooter"
import { LoadingOverlayDeprecated } from "../../../common/LoadingOverlayDeprecated"
import type { AggregatorType } from "./aggregatorTypeEnum"
import { AggregatorTypeEnum } from "./aggregatorTypeEnum"
import UploadImage from "../../../common/UploadImageLegacy"
import { getMibUrlAddress } from "../../../../utils/environment"
import { ImagePreviewComponent } from "../../../common/ImagePreview"

export type AggregatorFormItem = {
    terminalId: string
    name: string
    aggregatorType: Maybe<AggregatorType>
    isActive: boolean
    description: Maybe<string>
    storeLogo: Maybe<string>
    storeImages: Maybe<string[]>
    location: {
        id: string
        name?: string
    }
    metadata?: Maybe<ServiceMetadata>
}

export type ServiceMetadata = {
    onlineOrdering: {
        status: ServiceOnlineOrderingStatus
        priceMultiplier?: number
        shouldRoundUpPrices?: boolean
    }
}

export enum ServiceOnlineOrderingStatus {
    Pending = "pending",
    Active = "active",
    Paused = "paused",
    PausedForPickup = "paused-for-pickup",
    PausedForDelivery = "paused-for-delivery",
}

export const formItemLayout = {
    colon: false,
    labelCol: {
        xs: {
            span: 24,
        },
        sm: {
            span: 8,
        },
    },
    wrapperCol: {
        xs: {
            span: 24,
        },
        sm: {
            span: 16,
        },
    },
}
type SidepanelFormProps = {
    handleSubmit: () => void
    children: any
}

export const SidepanelForm = ({ handleSubmit, children }: SidepanelFormProps) => (
    <Form onSubmit={handleSubmit}>
        <div
            style={{
                padding: 24,
                paddingTop: 36,
            }}>
            {children}
        </div>
    </Form>
)

type NameFormItemProps = {
    initialValue: string
    form: WrappedFormUtils
}

export const NameFormItem = ({ initialValue, form }: NameFormItemProps) => {
    const nameOptions = {
        rules: [
            {
                max: 50,
                required: true,
            },
        ],
        initialValue: initialValue,
    }
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Name">
            <Row>
                <Col span={22}>
                    {form.getFieldDecorator("name", nameOptions)(<Input onFocus={e => e.target.select()} />)}
                </Col>
            </Row>
        </Form.Item>
    )
}

type MerchantIdFormItemProps = {
    initialValue: Maybe<string>
    form: WrappedFormUtils
}

export const MerchantIdFormItem = ({ initialValue, form }: MerchantIdFormItemProps) => {
    const options = {
        rules: [
            {
                required: form.getFieldValue("aggregatorType") === AggregatorTypeEnum.Grubhub,
            },
        ],
        initialValue: initialValue,
    }
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Merchant ID">
            <Row>
                <Col span={22}>
                    {form.getFieldDecorator("merchantId", options)(<Input onFocus={e => e.target.select()} />)}
                </Col>
            </Row>
        </Form.Item>
    )
}

type StoreLogoFormItemProps = {
    form: WrappedFormUtils
    logo: Maybe<string>
    onLogoChange: (logoPath: Maybe<string>) => void
}

export const StoreLogoFormItem = ({ logo, onLogoChange }: StoreLogoFormItemProps) => {
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Store Logo">
            <Row>
                <Col span={22}>
                    {logo ? (
                        <ImagePreviewComponent
                            alt="Store Logo"
                            logo={getAssetsImageFullUrl(logo)}
                            onRemove={() => onLogoChange(null)}
                        />
                    ) : (
                        <UploadImage title="Upload Logo" onUpload={filePath => onLogoChange(filePath)} />
                    )}
                </Col>
            </Row>
        </Form.Item>
    )
}
type StoreImagesFormItemProps = {
    form: WrappedFormUtils
    images: Maybe<string[]>
    onImageRemove: (imageIndex: number) => void
    onImageAdd: (imageUrl: string) => void
}

export const StoreImagesFormItem = ({ images, onImageRemove, onImageAdd }: StoreImagesFormItemProps) => {
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Store Images">
            <Row>
                <Col span={22}>
                    {(images || []).map((i, imageIndex) => (
                        <ImagePreviewComponent
                            key={i}
                            alt="Store Logo"
                            logo={getAssetsImageFullUrl(i)}
                            onRemove={() => {
                                onImageRemove(imageIndex)
                            }}
                            squareSize={70}
                            style={storeImagesStyles.previewImage}
                        />
                    ))}

                    <UploadImage title="Upload Store Images" onUpload={onImageAdd} />
                </Col>
            </Row>
        </Form.Item>
    )
}
const storeImagesStyles = {
    previewImage: {
        marginRight: "0.5rem",
        marginBottom: "0.5rem",
    },
}
type StoreDescriptionFormItemProps = {
    form: WrappedFormUtils
    initialValue: Maybe<string>
}

export const StoreDescriptionFormItem = ({ form, initialValue }: StoreDescriptionFormItemProps) => {
    const nameOptions = {
        rules: [
            {
                max: 250,
            },
        ],
        initialValue: initialValue,
    }
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Description">
            <Row>
                <Col span={22}>
                    {form.getFieldDecorator(
                        "description",
                        nameOptions
                    )(
                        <Input.TextArea
                            onFocus={e => e.target.select()}
                            placeholder="Enter Store Description"
                            rows={4}
                        />
                    )}
                </Col>
            </Row>
        </Form.Item>
    )
}
type LoadingSidepanelProps = {
    onCloseClick: () => void
    caption?: any
}

export const LoadingSidepanel = ({ onCloseClick, caption = null }: LoadingSidepanelProps) => (
    <div>
        <DrawerDetailFormHeader
            title={
                <div
                    style={{
                        display: "inline-block",
                    }}>
                    Aggregator
                </div>
            }
            isNew={false}
            onCancelClicked={onCloseClick}
        />

        <DrawerDetailFormFooter
            onCancelClicked={onCloseClick}
            onSubmitClicked={() => {}}
            saveButtonLabel=""
            saveDisabled={true}
            showDeleteButton={false}
        />

        <div
            style={{
                position: "relative",
                width: "100%",
            }}>
            <LoadingOverlayDeprecated
                style={{
                    position: "relative",
                }}
                caption={caption}
            />
        </div>
    </div>
)

export const getCode = () => {
    return getQueryStringParam("code")
}

export const getService = () => {
    return getQueryStringParam("service")
}

export const getQueryStringParam = (param: string) => {
    if (typeof window === "undefined") {
        return null
    }

    const search = new URLSearchParams(window.location.search)
    return search.get(param)
}

type AggregatorTypeFormItemProps = {
    form: WrappedFormUtils
}

export const AggregatorTypeFormItem = ({ form }: AggregatorTypeFormItemProps) => {
    const aggregatorOptions = {
        rules: [
            {
                required: true,
            },
        ],
    }
    return (
        <Form.Item
            {...formItemLayout}
            style={{
                marginBottom: "1rem",
            }}
            label="Aggregator">
            <Row>
                <Col span={22}>
                    {form.getFieldDecorator(
                        "aggregatorType",
                        aggregatorOptions
                    )(
                        <Radio.Group
                            style={{
                                marginTop: "7px",
                            }}>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.Uber}>
                                Uber Eats
                            </Radio>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.Grubhub}>
                                Grubhub (Seamless)
                            </Radio>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.DoorDash}>
                                DoorDash
                            </Radio>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.Slice}>
                                Slice
                            </Radio>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.WestSideEats}>
                                WestSideEats
                            </Radio>
                            <Radio style={aggregatorTypeRadioStyle} value={AggregatorTypeEnum.Foodmeow}>
                                K-town
                            </Radio>
                        </Radio.Group>
                    )}
                </Col>
            </Row>
        </Form.Item>
    )
}
const aggregatorTypeRadioStyle = {
    display: "block",
    width: "100%",
    marginBottom: ".4rem",
}

const getAssetsImageFullUrl = (relativePath: string): string => {
    if (!relativePath.includes("http")) {
        return getMibUrlAddress("/" + relativePath, null) || ""
    }

    return relativePath
}
